import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
// Eager: top entry routes — keep these in the initial bundle so they render
// without a Suspense fallback flash for the most common landings.
import Index from "./pages/Index";
import SearchResults from "./pages/SearchResults";
import Login from "./pages/Login";
import SavedEpisodes from "./pages/SavedEpisodes";
import NotFound from "./pages/NotFound";

// Lazy: less-frequent routes. Loaded on demand to shrink the initial JS bundle.
// Each chunk hydrates behind a Suspense fallback that matches the page-shell bg
// so the swap is visually quiet (no white flash, no layout shift).
const WelcomeModal = lazy(() => import("./components/WelcomeModal"));
const InstallAppModal = lazy(() => import("./components/InstallAppModal"));
const OfflineQueueBadge = lazy(() => import("./components/OfflineQueueBadge").then(m => ({ default: m.OfflineQueueBadge })));
import GlobalAudioHost from "./components/GlobalAudioHost";
import MiniPlayerBar from "./components/MiniPlayerBar";
import BackgroundAudioHint from "./components/BackgroundAudioHint";
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Admin = lazy(() => import("./pages/Admin"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const PublicPlaylist = lazy(() => import("./pages/PublicPlaylist"));
const EpisodePage = lazy(() => import("./pages/EpisodePage"));
const ShowPage = lazy(() => import("./pages/ShowPage"));
const TopicPage = lazy(() => import("./pages/TopicPage"));
const AnswerPage = lazy(() => import("./pages/AnswerPage"));
const AnswersIndex = lazy(() => import("./pages/AnswersIndex"));
const Profile = lazy(() => import("./pages/Profile"));
const Today = lazy(() => import("./pages/Today"));
const HostMockup = lazy(() => import("./pages/HostMockup"));
const Staff = lazy(() => import("./pages/Staff"));
const StaffLogin = lazy(() => import("./pages/StaffLogin"));
const StaffPeople = lazy(() => import("./pages/StaffPeople"));
const PersonPage = lazy(() => import("./pages/PersonPage"));
const PeopleIndex = lazy(() => import("./pages/PeopleIndex"));
const StylePreview = lazy(() => import("./pages/StylePreview"));
const ForAiAgents = lazy(() => import("./pages/ForAiAgents"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));

// Tuned React Query defaults: dedupe within a minute, GC after 5 min. Prevents
// silent re-fetches on every mount/focus for hooks that don't set their own.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, gcTime: 5 * 60_000, refetchOnWindowFocus: false },
  },
});

// Quiet Suspense fallback — matches the retro paper background so swap-in
// from a lazy chunk is invisible to the user.
function RouteFallback() {
  return <div className="min-h-screen retro-grid-bg" aria-hidden />;
}

// Legacy URL shape emitted by older expert-answer link_maps:
//   /podcasts/:podcastSlug/episodes/:episodeSlug  →  /episode/:episodeSlug
//   /podcasts/:slug                                →  /show/:slug
function LegacyEpisodeRedirect() {
  const { episodeSlug } = useParams();
  return <Navigate to={`/episode/${episodeSlug ?? ""}`} replace />;
}
function LegacyPodcastRedirect() {
  const { podcastSlug } = useParams();
  return <Navigate to={`/show/${podcastSlug ?? ""}`} replace />;
}
// /person/:slug was used by the first generation of person profile links —
// keep them working by redirecting to the canonical /people/:slug.
function LegacyPersonRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/people/${slug ?? ""}`} replace />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <WelcomeModal />
          <InstallAppModal />
          <OfflineQueueBadge />
          <GlobalAudioHost />
          <MiniPlayerBar />
          <BackgroundAudioHint />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/today" element={<Today />} />
              <Route path="/search" element={<SearchResults />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/saved" element={<SavedEpisodes />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin/host-mockup" element={<HostMockup />} />
              <Route path="/staff" element={<Staff />} />
              <Route path="/staff/login" element={<StaffLogin />} />
              <Route path="/staff/people" element={<StaffPeople />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/episode/:slug" element={<EpisodePage />} />
              <Route path="/show/:slug" element={<ShowPage />} />
              <Route path="/topic/:slug" element={<TopicPage />} />
              <Route path="/answer/:slug" element={<AnswerPage />} />
              <Route path="/answers" element={<AnswersIndex />} />
              <Route path="/list/:slug" element={<PublicPlaylist />} />
              <Route path="/people" element={<PeopleIndex />} />
              <Route path="/people/:slug" element={<PersonPage />} />
              <Route path="/dev/style-preview" element={<StylePreview />} />
              <Route path="/for-ai-agents" element={<ForAiAgents />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              {/* Legacy redirects — keep old answer link_map URLs working */}
              <Route path="/podcasts/:podcastSlug/episodes/:episodeSlug" element={<LegacyEpisodeRedirect />} />
              <Route path="/podcasts/:podcastSlug" element={<LegacyPodcastRedirect />} />
              <Route path="/person/:slug" element={<LegacyPersonRedirect />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
